import { type FC, type MouseEvent, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { Box, Text } from '@factorialco/deprecated-design-system'
import { TwoColumnLayout } from '@factorialco/f0-react'
import { OneEmptyState, Page, Spinner } from '@factorialco/f0-react/dist/experimental'
import { DistributorProductVariantsDocument } from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import { DeviceVariantSelector } from 'modules/it_management/components/DeviceVariantSelector'
import { DeviceImageCarousel } from 'modules/it_management/components/DeviceImageCarousel'
import {
  DeviceAttribute,
  DeviceSpecifications,
} from 'modules/it_management/components/DeviceSpecifications'
import { useItManagementFeatureAvailability } from 'modules/it_management/hooks/useItManagementFeatureAvailability'
import { useProductCatalog } from 'modules/it_management/lib/graphqlProductCatalogContext'

import emptyStateImage from './Empty-state-device-catalog-card.png'
import { ResponsivePage } from 'modules/layout/components/ResponsivePage'
import { useQuery } from 'modules/core/hooks/useQuery'
import { DeviceVariant } from 'modules/it_management/components/DeviceVariantSelector/types'
import RequestDeviceModal from 'modules/it_management/components/RequestDeviceModal'

const DeviceCatalogDetailPage: FC = () => {
  const navigate = useNavigate()
  const { variantId } = useParams<{ variantId: string }>()
  const { queryContext, buildProductImageUrl, loadingProductCatalogContext } = useProductCatalog()
  const { company, access } = useCurrentSession()
  const [showRequestModal, setShowRequestModal] = useState(false)

  const {
    isItManagementEnabled,
    loading: featureLoading,
    error: featureError,
  } = useItManagementFeatureAvailability()

  const { data, loading, error } = useQuery(DistributorProductVariantsDocument, {
    variables: { ids: variantId ? [variantId] : [] },
    skip: !variantId || loadingProductCatalogContext,
    context: queryContext,
  })

  const variant = data?.itManagement.distributorProductVariantsConnection?.nodes?.[0] || null

  useEffect(() => {
    if (variant) {
      track(trackingEvents.itManagement.deviceCatalogEvents.goToDeviceDetail, {
        device_id: variant.id,
        device_name: variant.product?.name || variant.sku || variant.id,
        coming_from: document.referrer || 'direct',
        company_id: company.id,
        user_id: access.id,
      })
    }
  }, [variant, company.id, access.id])

  if (featureLoading || loadingProductCatalogContext || loading) {
    return (
      <Page>
        <Box flexDirection="row" alignItems="center" justifyContent="center" padding="s48">
          <Spinner size="large" />
        </Box>
      </Page>
    )
  }

  if (featureError || error || !variant) {
    return (
      <Page>
        <Box padding="s24">
          <OneEmptyState
            title={i18n.t('it_management.device_catalog.detail.not_found')}
            description={i18n.t('it_management.device_catalog.detail.not_found_description')}
            variant="critical"
          />
        </Box>
      </Page>
    )
  }

  const deviceName = variant.product?.name ? variant.product.name : variant.sku || variant.id
  const productMediaImages: string[] =
    'productMediaImageUrls' in variant && Array.isArray(variant.productMediaImageUrls)
      ? variant.productMediaImageUrls.map((url) => buildProductImageUrl(url))
      : []
  const deviceImages =
    productMediaImages.length > 0
      ? productMediaImages
      : [buildProductImageUrl(variant.firstProductMediaImageUrl, emptyStateImage)]

  const handleRequestClick = (e?: MouseEvent) => {
    e?.stopPropagation()
    track(trackingEvents.itManagement.deviceCatalogEvents.requestDeviceStart, {
      device_id: variant.id,
      device_name: deviceName,
      origin: 'detail_page',
      company_id: company.id,
      user_id: access.id,
    })

    setShowRequestModal(true)
  }

  const deviceAttributes: DeviceAttribute[] =
    'productAttributes' in variant && Array.isArray(variant.productAttributes)
      ? variant.productAttributes.map((attr: { id: string; name: string; value: string }) => ({
          id: attr.id,
          name: attr.name,
          value: attr.value,
        }))
      : []

  /*
    THIS IS MOCK DATA FOR THE VARIANT SELECTOR, AS WE DON'T HAVE REAL VARIANTS IN THE API YET. SHOULD BE REPLACED WITH REAL DATA ONCE AVAILABLE.
  */
  const variants: DeviceVariant[] = [
    {
      id: 'model',
      title: 'Model',
      required: true,
      type: 'radio',
      variants: [
        {
          id: '13-inch',
          name: '13-inch',
          description: 'From 999€ or 83.25€/mo. for 12 mo.',
        },
        {
          id: '15-inch',
          name: '15-inch',
          description: 'From 1299€ or 108.25€/mo. for 12 mo.',
        },
      ],
    },
    {
      id: 'color',
      title: 'Color',
      required: true,
      type: 'color-select',
      variants: [
        {
          id: 'space-gray',
          color: '#C4DAE2',
        },
        {
          id: 'silver',
          color: '#DFE1E2',
        },
        {
          id: 'gold',
          color: '#F3E6D2',
        },
        {
          id: 'blue',
          color: '#2D3643',
        },
      ],
    },
    {
      id: 'storage',
      title: 'Storage',
      required: true,
      type: 'radio',
      variants: [
        {
          id: '512GB',
          name: '512GB',
          description: 'Included',
        },
        {
          id: '1TB',
          name: '1TB',
          description: '+200€',
        },
        {
          id: '2TB',
          name: '1TB',
          description: '+600€',
        },
      ],
    },
    {
      id: 'keyboard-language',
      title: 'Keyboard language',
      required: true,
      type: 'select',
      variants: [
        {
          id: 'english',
          name: 'English',
        },
        {
          id: 'french',
          name: 'French',
        },
        {
          id: 'spanish',
          name: 'Spanish',
        },
      ],
    },
    {
      id: 'install-mdm-license',
      title: 'Factorial IT MDM License',
      required: true,
      type: 'radio',
      variants: [
        {
          id: 'with-mdm',
          name: 'No, thanks',
        },
        {
          id: 'without-mdm',
          name: 'Yes, preinstall on my device.',
          description: '+10€/mo.',
        },
      ],
    },
    {
      id: 'payment-options',
      title: 'Payment options',
      required: true,
      type: 'radio',
      variants: [
        {
          id: 'leasing',
          name: 'Leasing',
          description: '€36,99/mo.',
        },
        {
          id: 'purchase',
          name: 'Purchase',
          description: 'Or pay in installments with Santander Credit Card',
        },
      ],
    },
  ]

  const variantDefaultValues = {
    model: '13-inch',
    color: 'space-gray',
    'keyboard-language': 'english',
  }

  return (
    <ResponsivePage.Body
      authorized={isItManagementEnabled}
      error={featureError}
      loading={featureLoading || loadingProductCatalogContext}
      height="full"
    >
      <TwoColumnLayout
        mainColumnPosition="left"
        sticky
        sideContent={
          <DeviceVariantSelector
            settings={variants}
            defaultValues={variantDefaultValues}
            onSubmit={handleRequestClick}
          />
        }
      >
        <Box flexDirection="column" style={{ flex: '1 1 auto', minWidth: '0' }}>
          <DeviceImageCarousel images={deviceImages} />

          {variant.product?.description && deviceAttributes.length > 0 && (
            <Box gap="s16" flexDirection="column" paddingY="s24">
              <Text size="200" weight="normal" color="black">
                {variant.product.description}
              </Text>
            </Box>
          )}

          <DeviceSpecifications attributes={deviceAttributes} />

          {showRequestModal && (
            <RequestDeviceModal
              variant={variant}
              onClose={() => {
                setShowRequestModal(false)
                navigate(`/device-catalog`)
              }}
            />
          )}
        </Box>
      </TwoColumnLayout>
    </ResponsivePage.Body>
  )
}

export default DeviceCatalogDetailPage
